import { CircularProgress, Select, TextField, Theme, Typography, WithStyles, createStyles, withStyles } from "@material-ui/core";
import { IStoreState, ThunkDispatch } from "../../../modules";
import React, { ChangeEvent, Fragment } from "react";
import { browseQuestions, getEnrolledCredentials, setQuestions } from '../../../modules/authmethods';

import GenericEnroll from "./GenericEnroll";
import PropTypes from 'prop-types';
import _ from 'lodash';
import { bindActionCreators } from "redux";
import { connect } from "react-redux";

const styles = (theme: Theme) => createStyles({
  questionForm: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    margin: `${theme.spacing(3)}px ${theme.spacing(2)}px`,
  },
  question: {
    marginBottom: theme.spacing(2)
  }
})

type QuestionsEnrollState = {
  questionList: Question[][]
  questionIds: string[]
  questionTexts: string[]
  actualAnswers: string[]
  error: string
  errorFields: { [field: string]: boolean }
  isDirty: boolean
  loading: boolean
}

type Question = {
  questionId: string
  questionText: string
}

let initialState = {
  questionList: [[] as Question[], [] as Question[], [] as Question[]],
  questionIds: ["", "", ""],
  questionTexts: ["", "", ""],
  actualAnswers: ["", "", ""],
  error: "",
  errorFields: {} as { [field: string]: boolean },
  isDirty: false,
  loading: true,
}

class QuestionsEnroll extends React.Component<Props, QuestionsEnrollState> {
  static propTypes = {
    expanded: PropTypes.bool.isRequired,
    setExpanded: PropTypes.func.isRequired
  }

  state = _.clone(initialState)

  componentDidMount = () => {
    let challengeQuestions = this.props.authmethods.challengeQuestions.challengeQuestions
    this.props.browseQuestions().then((questionList: Question[][]) => {
      let questionIds: string[] = []
      let questionTexts: string[] = []
      let actualAnswers: string[] = []

      questionList.forEach(questionList => {
        let answer = challengeQuestions.find(answer => questionList.some(question => answer.questionId === question.questionId))
        questionIds.push((answer && answer.questionId) || "")
        questionTexts.push((answer && answer.questionText) || "")
        actualAnswers.push((answer && answer.actualAnswer) || "")
      })

      initialState = _.merge(initialState, {
        questionList,
        questionIds,
        questionTexts,
        actualAnswers,
        loading: false,
      })
      this.setState(initialState)
    }).catch((error: Error) => {
      console.error(error)
      this.setState({ error: "Error retrieving user questions" })
    })
  }

  handleCancel = () => {
    this.setState(initialState)
    this.props.setExpanded(false)
  }

  handleSave = () => {
    let list = _.zipWith(this.state.questionIds, this.state.questionTexts, this.state.actualAnswers, (questionId, questionText, actualAnswer) => ({
      questionId,
      questionText,
      actualAnswer,
    }))

    let errorFields: { [field: string]: boolean } = {}
    list.forEach((q, i) => {
      if (q.questionId === "")
        errorFields['question' + i] = true
      if (q.actualAnswer === "")
        errorFields['answer' + i] = true
    });

    let errorCount = Object.keys(errorFields).length

    this.setState({
      errorFields,
      error: errorCount > 0 ? "Please fill in all questions and answers" : "",
    })

    if (errorCount > 0)
      return

    this.props.setQuestions(list).then((data: any) => {
      this.props.setExpanded(false)
      return data
    }).catch((error: Error) => {
      console.error(error)
      this.setState({ error: "Error setting user questions" })
    })
  }

  handleQuestionChange = (group: number) => (event: any) => {
    const questionId = event.target.value
    const questionText = this.state.questionList[group].filter(q => q.questionId === questionId)[0].questionText
    this.setState(prevState => {
      let newState: QuestionsEnrollState = _.merge({}, prevState)
      newState.questionIds[group] = questionId
      newState.questionTexts[group] = questionText
      newState.isDirty = true
      return newState
    })
  }

  handleAnswerChange = (qnNum: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const userAnswer = event.target.value
    this.setState(prevState => {
      let newState: QuestionsEnrollState = _.merge({}, prevState)
      newState.actualAnswers[qnNum] = userAnswer
      newState.isDirty = true
      return newState
    })
  }

  QuestionGroup = ({group}: { group: number }) => {
    const { classes } = this.props
    const { questionList, questionIds, actualAnswers, errorFields } = this.state

    return <Fragment>
      <div className={classes.questionForm}>
        <Select
          native
          value={questionIds[group]}
          onChange={this.handleQuestionChange(group)}
          className={classes.question}
          error={errorFields['question' + group]}
        >
          <option value="" disabled>Question {group + 1}</option>
          {
            questionList[group].map(question =>
              <option key={question.questionId} value={question.questionId}>{question.questionText}</option>
            )
          }
        </Select>
        <TextField
          value={actualAnswers[group]}
          placeholder="Answer"
          onChange={this.handleAnswerChange(group)}
          error={errorFields['answer' + group]}
        />
      </div>
    </Fragment>
  }

  renderForm = () => {
    return (this.state.loading ?
      <CircularProgress />
      : <Fragment>
        <Typography>Please Select and Answer 3 Security Questions</Typography>

        <this.QuestionGroup group={0}></this.QuestionGroup>

        {(this.state.questionIds[0] || this.state.actualAnswers[0]) &&
          <this.QuestionGroup group={1}></this.QuestionGroup>
        }
        {(this.state.questionIds[1] || this.state.actualAnswers[1]) &&
          <this.QuestionGroup group={2}></this.QuestionGroup>
        }
      </Fragment>
    )
  }

  render = () =>
    <GenericEnroll
      authMethodName="Challenge Questions"
      enrolled={this.props.authmethods.challengeQuestions.enrolled}
      renderForm={this.renderForm}
      handleSave={this.handleSave}
      handleCancel={this.handleCancel}
      expanded={this.props.expanded}
      setExpanded={this.props.setExpanded}
      error={this.state.error}
      dirty={this.state.isDirty} />
}

const mapStateToProps = ({ accounts, intl, authmethods }: IStoreState) => ({
  accounts,
  authmethods,
  intl,
})

const mapDispatchToProps = (dispatch: ThunkDispatch) =>
  bindActionCreators(
    {
      browseQuestions,
      setQuestions,
      getEnrolledCredentials
    }, dispatch
  )

interface Props extends WithStyles<typeof styles>, ReturnType<typeof mapStateToProps>, ReturnType<typeof mapDispatchToProps> {
  expanded: boolean
  setExpanded: (expanded: boolean) => void
  expandNext: () => void
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(QuestionsEnroll))